/**
 * Truy cập nhật ký thao tác (audit-service, `openapi/platform-api.yaml` tag
 * `audit`).
 *
 * Cổng tenant gọi `GET /api/v1/tenants/{id}/audit`, cổng nền tảng gọi
 * `GET /api/v1/audit?tenant_id=` — cùng hình dạng trang, nên một hàm phục vụ
 * cả hai qua cờ `nenTang`. Mock lọc đúng như backend: khoảng thời gian, người
 * dùng, thao tác (mã khớp đúng hoặc tiền tố), quyết định; sắp `created_at` giảm
 * dần; con trỏ là vị trí.
 */
import { dungApiThat, goiApi } from '../api/http';
import type { TrangCon } from '../api/phan-trang';
import { nguoiDungRepository } from './nguoi-dung.repository';
import { suKienTuApi } from '../../modules/nhat-ky/nghiep-vu/anh-xa-api';
import { khopLoc } from '../../modules/nhat-ky/nghiep-vu/danh-muc-hanh-dong';
import type { AuditRecordApi } from '../../modules/nhat-ky/nghiep-vu/anh-xa-api';
import { MOCK_AUDIT } from '../../mock/nhat-ky';
import { mockRequest, mockState } from '../../mock/transport';
import type { LocAudit, TrangAudit, TrangNguoiDungRutGon } from '../../modules/nhat-ky/types';

/** Một trang thật. `tenant_id` ở cổng tenant nằm trên đường dẫn, không gửi lại ở query. */
async function trangThat(
  loc: LocAudit,
  cursor: string | null,
  limit: number,
  nenTang: boolean,
): Promise<TrangAudit> {
  const query = {
    tenant_id: nenTang ? loc.tenant_id : undefined,
    from: loc.from,
    to: loc.to,
    user_id: loc.user_id?.trim() || undefined,
    action: loc.actions?.length ? loc.actions : undefined,
    action_prefix: loc.action_prefixes?.length ? loc.action_prefixes : undefined,
    decision: loc.decision,
    limit,
    cursor: cursor ?? undefined,
  };
  const duongDan = nenTang
    ? '/api/v1/audit'
    : `/api/v1/tenants/${encodeURIComponent(loc.tenant_id)}/audit`;
  const trang = await goiApi<TrangCon<AuditRecordApi>>(duongDan, { query });
  return { items: trang.items.map(suKienTuApi), next_cursor: trang.next_cursor };
}

export const nhatKyRepository = {
  trang: (
    loc: LocAudit,
    cursor: string | null,
    limit: number,
    // Hai endpoint khác đường dẫn, cùng kết quả; mock không cần phân biệt.
    nenTang: boolean,
  ): Promise<TrangAudit> => {
    if (dungApiThat()) return trangThat(loc, cursor, limit, nenTang);
    if (mockState() === 'filtered-empty') return mockRequest({ items: [], next_cursor: null });

    const tu = Date.parse(loc.from);
    const den = Date.parse(loc.to);
    const nguoi = loc.user_id?.trim();
    const ds = MOCK_AUDIT.filter((s) => {
      const t = Date.parse(s.created_at);
      return (
        s.tenant_id === loc.tenant_id &&
        t >= tu &&
        t <= den &&
        (!nguoi || s.user_id === nguoi) &&
        khopLoc(s.action, loc.actions ?? [], loc.action_prefixes ?? []) &&
        (!loc.decision || s.decision === loc.decision)
      );
    }).sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));

    const batDau = cursor ? Number(cursor) : 0;
    return mockRequest(
      {
        items: ds.slice(batDau, batDau + limit),
        next_cursor: batDau + limit < ds.length ? String(batDau + limit) : null,
      },
      { items: [], next_cursor: null },
    );
  },

  /**
   * `GET /api/v1/tenants/{id}/users` — người dùng rút gọn của một tenant cho ô lọc ở cổng nền tảng; cùng đường với tab
   * Người dùng ở chi tiết tenant (`nguoiDungRepository.cuaTenant`).
   */
  nguoiDungCuaTenant: (
    tenantId: string,
    search: string,
    cursor: string | null,
    limit: number,
  ): Promise<TrangNguoiDungRutGon> => nguoiDungRepository.cuaTenant(tenantId, { search, cursor, limit }),
};
